import {inject, Injectable, signal} from '@angular/core'
import {catchError, concat, map, NEVER, Observable, of, retry, switchMap, tap, toArray} from 'rxjs'
import {HttpClient, HttpHeaders} from '@angular/common/http'
import {LOCAL_STORAGE} from '../0-general/providers'
import {
  IGitLabBranch,
  IGitLabCreateBranchResult,
  IGitLabCreateMRResult,
  IGitLabCreateReleaseResult, IGitlabFileListItem,
  IGitlabFileResult,
  IGitLabGroup,
  IGitLabProject,
  IGitLabProjectSettings,
  IGitLabSaveFileResult,
  TVersion
} from './gitlab'
import {MatDialog} from '@angular/material/dialog'
import {IWaitDialog, WaitDialog} from '../0-general/wait-dialog/wait-dialog'
import {GitlabTokenService} from './gitlab-token-service'

export const GITLAB_HOST = 'https://gitlab.com/api/v4'

const GROUPS_KEY = 'gitlabGroups'
const PROJECTS_KEY = 'gitlabProjects'

@Injectable({
  providedIn: 'root'
})
export class GitlabService {

  /* v8 ignore next @preserve */
  public projects = signal<IGitLabProject[]>([])
  /* v8 ignore next @preserve */
  public groups = signal<IGitLabGroup[]>([])

  private readonly storage = inject(LOCAL_STORAGE)

  private readonly http = inject(HttpClient)

  private readonly tokenService = inject(GitlabTokenService)

  private readonly dialog = inject(MatDialog)

  public listGroups(update = false): Observable<IGitLabGroup[]> {
    const stored = this.storage.getItem(GROUPS_KEY)
    if (!update && stored) {
      return of(JSON.parse(stored) as IGitLabGroup[])
    }
    const ref = this.dialog.open<WaitDialog, IWaitDialog, void>(WaitDialog, {
      data: {
        title: 'Updating groups',
        text: signal('This will take a minute'),
        closable: false
      },
      disableClose: true
    })
    const groups: IGitLabGroup[] = []

    const groupIds = [5335894, 61754934]

    const obs = groupIds.map(groupId => {
      return this.listGroupsInternal(groupId)
        .pipe(
          switchMap(res => {
            groups.push(...res)
            return this.getGroup(groupId)
          }),
          tap(group => {
            groups.push(group)
          })
        )
    })

    return concat(...obs).pipe(
      toArray(),
      map(() => {
        ref.close()
        this.groups.set(groups)
        this.storage.setItem(GROUPS_KEY, JSON.stringify(groups))
        return groups
      })
    )
  }

  public listProjects(update = false): Observable<IGitLabProject[]> {
    const q = new URLSearchParams({
      simple: 'true',
      membership: 'true',
      per_page: '100'
      //search: 'sparbankensyd'
    })
    const ref = this.dialog.open<WaitDialog, IWaitDialog, void>(WaitDialog, {
      data: {
        title: 'Updating projects',
        text: signal('This can take a few minutes.'),
        closable: false
      },
      disableClose: true
    })
    const url = `${GITLAB_HOST}/projects?${q.toString()}`

    return this.getWithCache<IGitLabProject>(PROJECTS_KEY, url, update)
      .pipe(
        tap(p => {
          this.projects.set(p)
          ref.close()
        })
      )
  }

  /**
   * Creates an MR from -> to, only squash when you need to.
   *
   * @param projectId
   * @param from
   * @param to
   * @param squash
   */
  public createMR(projectId: number, from: string, to: string, squash: boolean): Observable<IGitLabCreateMRResult> {
    const body = {
      id: projectId,
      source_branch: from,
      target_branch: to,
      title: 'Automated MR',
      squash: squash
    }

    const url = `${GITLAB_HOST}/projects/${projectId}/merge_requests`
    return this.getCredentials().pipe(
      switchMap(headers => {
        return this.http.post<IGitLabCreateMRResult>(url, body, {headers})
      })
    )
  }

  public acceptMR(projectId: number, iid: number, squash: boolean, pipeline: boolean) {
    const body = {
      id: projectId,
      merge_request_iid: iid,
      squash: squash,
      auto_merge: pipeline,
      should_remove_source_branch: false
    }

    const url = `${GITLAB_HOST}/projects/${projectId}/merge_requests/${iid}/merge`

    return this.getCredentials().pipe(
      switchMap(headers => {
        return this.http.put<IGitLabCreateReleaseResult>(url, body, {headers})
          .pipe(
            retry({count: 2, delay: 2000}),
            catchError((e) => {
              console.log('Error', e)
              this.dialog.closeAll()
              this.dialog.open<WaitDialog, IWaitDialog, void>(WaitDialog, {
                data: {
                  title: 'Merge Error',
                  text: signal(e.message),
                  closable: true
                },
                disableClose: true
              })
              return NEVER
            })
          )
      })
    )
  }

  /**
   * Get the status of an MR.
   */
  public checkMR(projectId: number, iid: number) {
    const url = `${GITLAB_HOST}/projects/${projectId}/merge_requests/${iid}`
    return this.getCredentials().pipe(
      switchMap(headers => {
        return this.http.get<IGitLabCreateMRResult>(url, {headers})
      })
    )
  }

  /**
   * Retrieve an arbitrary file from the repo.
   *
   * @param projectId
   * @param fileName - Name with full path no leading /
   * @param branch
   */
  public getFile(projectId: number, fileName: string, branch: string) {
    const url = `https://gitlab.com/api/v4/projects/${projectId}/repository/files/${encodeURIComponent(fileName)}?ref=${branch}`
    return this.getCredentials().pipe(
      switchMap(headers => {
        return this.http.get<IGitlabFileResult>(url, {headers})
      })
    )
  }

  public getFiles(projectId: number, branch: string) {
    const url = `https://gitlab.com/api/v4/projects/${projectId}/repository/tree?ref=${branch}&recursive=true&per_page=1000`
    return this.getCredentials().pipe(
      switchMap(headers => {
        return this.getItems<IGitlabFileListItem>(url, headers, [])
      })
    )
  }


  /**
   * Saves a file, basically creates a commit.
   * @param projectId
   * @param content
   * @param fileName
   * @param branch
   */
  public saveFile(projectId: number, content: any, fileName: string, branch: string) {
    const body = {
      file_path: encodeURIComponent(fileName),
      branch,
      encoding: 'base64',
      // Make sure it is pretty in the UI as well.
      content: globalThis.btoa(JSON.stringify(content, null, 2)),
      commit_message: 'Script update of package.json'
    }

    const url = `${GITLAB_HOST}/projects/${projectId}/repository/files/${encodeURIComponent(fileName)}`
    return this.getCredentials()
      .pipe(
        switchMap(headers => {
          return this.http.put<IGitLabSaveFileResult>(url, body, {headers})
        })
      )
  }

  public listReleases(projectId: number) {
    return this.listBranches(projectId)
      .pipe(map(branches => {
        return branches
          .filter(branch => new RegExp(/^release_v.*/).exec(branch.name))
          .map(branch => branch.name.split('_v')[1])
          .toSorted((a, b) =>
            b.localeCompare(a, undefined, {numeric: true}))
      }))
  }

  public listBranches(projectId: number): Observable<IGitLabBranch[]> {
    const query = new URLSearchParams({
      per_page: '100'
    }).toString()

    const url = `${GITLAB_HOST}/projects/${projectId}/repository/branches?${query}`
    return this.getCredentials()
      .pipe(
        switchMap(headers => {
          return this.getItems<IGitLabBranch>(url, headers, [])
        })
      )
  }

  /**
   * Create any branch from -> to at will.
   *
   * @param projectId
   * @param from
   * @param to
   */
  public createBranch(projectId: number, from: string, to: string): Observable<any> {
    const url = `${GITLAB_HOST}/projects/${projectId}/repository/branches?branch=${to}&ref=${from}`
    return this.getCredentials()
      .pipe(
        switchMap(headers => {
          return this.http.post<IGitLabCreateBranchResult>(url, {}, {headers})
        })
      )
  }

  /**
   * Create a "release", a zip file of the current state. It s saved
   * independently of the git tree. Whilst "tags" are part of the git.
   *
   * We create the release based on the "tag", so better be sure to have
   * created at tag first. Deleting the "tag" and/or the branch will retain
   * the release.
   *
   * @param projectId - The project in question.
   * @param tagName - What we crate the release from.
   */
  public createRelease(projectId: number, tagName: string) {
    const body = {
      id: projectId,
      tag_name: tagName,
      name: 'Release ' + tagName,
      description: 'Automated release'
    }

    const url = `${GITLAB_HOST}/projects/${projectId}/releases`
    return this.getCredentials().pipe(
      switchMap(headers => {
        return this.http.post(url, body, {headers})
      })
    )
  }

  /**
   * Create a "tag", a tag is retained even if you delete the "branch".
   * We normally name tags "vX.Y.Z" e.g. v1.2.3 to match what we store in
   * "releases" (see above).
   *
   * Tags can be checked out, whilst the "release" is a zipped reference to
   * the same.
   *
   * We set the tag on "master" _after_ we have merged the release branch.
   *
   * @param projectId
   * @param tagName
   * @param branch
   */
  public createTag(projectId: number, tagName: string, branch: string) {
    const body = {
      id: projectId,
      tag_name: tagName,
      ref: branch,
      message: 'Bot tag created'
    }

    const url = `${GITLAB_HOST}/projects/${projectId}/repository/tags`
    return this.getCredentials().pipe(
      switchMap(headers => {
        return this.http.post(url, body, {headers})
      })
    )
  }

  /**
   * Rudimentary but working "semver", returns a new version.
   * @param version - The version as a string "1.2.2"
   * @param level MINOR | MAJOR | PATCH
   */
  public updateVersion(version: string, level: TVersion) {
    const parts: string[] = version.split('.')
    let pos = 0 // Major

    if (level === 'MAJOR') {
      parts[1] = '0'
      parts[2] = '0'
    } else if (level === 'MINOR') {
      pos = 1
      parts[2] = '0'
    } else {
      pos = 2
    }
    parts[pos] = `${Number.parseInt(parts[pos]) + 1}`
    return parts.join('.')
  }

  /**
   * Get a full project
   * @param projectId
   */
  public getProject(projectId: number): Observable<IGitLabProjectSettings> {
    const url = `${GITLAB_HOST}/projects/${projectId}`
    return this.getCredentials().pipe(
      switchMap(headers => {
        return this.http.get<IGitLabProjectSettings>(url, {headers})
      })
    )
  }

  public updateProject(projectId: number, setting: keyof IGitLabProjectSettings, value: string | boolean): Observable<IGitLabProjectSettings> {
    const url = `${GITLAB_HOST}/projects/${projectId}`
    const data = {
      [setting]: value
    }
    return this.getCredentials().pipe(
      switchMap(headers => {
        return this.http.put<IGitLabProjectSettings>(url, data, {headers})
      })
    )
  }

  private getGroup(id: number): Observable<IGitLabGroup> {
    const url = `${GITLAB_HOST}/groups/${id}`
    return this.getCredentials()
      .pipe(
        switchMap(headers => {
          return this.http.get<IGitLabGroup>(url, {headers})
        })
      )
  }

  private listGroupsInternal(id: number): Observable<IGitLabGroup[]> {

    const q = new URLSearchParams({
      per_page: '100'
    }).toString()

    const url = `${GITLAB_HOST}/groups/${id}/descendant_groups?${q}` //${groupId}/projects?all_available=true`
    return this.getCredentials()
      .pipe(
        switchMap(headers => {
          return this.http.get<IGitLabGroup[]>(url, {headers})
        })
      )
  }


  /**
   * Invokes and set the result in the "cache" (local storage). If not
   * explicitly force an update, returns the cached version.
   * @param key - The cache item key
   * @param url
   * @param update
   */
  private readonly getWithCache = <T>(key: string, url: string, update = false): Observable<T[]> => {
    const stored = this.storage.getItem(key)
    if (!update && stored) {
      return of(JSON.parse(stored) as T[])
    }
    return this.getCredentials()
      .pipe(
        switchMap(headers => {
          return this.getItems<T>(url, headers, [])
        }),
        tap(items => this.storage.setItem(key, JSON.stringify(items)))
      )
  }

  /**
   * Highly opportunistic
   *
   * @param url - The start URL, we will continue by nexts.
   * @param headers - The headers you want to _send_ (authorization)
   * @param items - An array that will be filled with items of type T.
   * @private
   */
  private getItems<T>(url: string, headers: HttpHeaders, items: T[]): Observable<T[]> {
    return this.http.get(url, {headers, observe: 'response'}).pipe(
      switchMap(res => {
        items.push(...(res.body as T[]))
        const next = this.resolveNext(res.headers)
        if (next) {
          return this.getItems(next, headers, items)
        }
        return of(items)
      })
    )
  }

  /**
   * Look in headers to see if this has continue or not.
   * @param headers
   * @private
   */
  private resolveNext(headers: HttpHeaders): string | undefined {
    if (!headers.has('link')) {
      return
    }

    const items = headers.get('link')!.split(',')
      .filter(i => i.includes('rel="next"'))
      .map(i => {
        const parts = i.split(';')
        return /<(.*)>/.exec(parts[0])![1]
      })
    return items[0]
  }

  private getCredentials(): Observable<HttpHeaders> {
    return this.tokenService.setCredentials()
      .pipe(
        map(res =>
          new HttpHeaders({'Authorization': `Bearer ${res.access_token}`}))
      )
  }
}
